// Open House front porch — the guest page's transport and price helpers: the
// guest draft (OpenHouse::DraftsController, opened by its capability cookie and
// saved by revision) and the exact quote (OpenHouse::QuotesController). Same
// transport rules as actions.js: fetch with the CSRF header, never an Inertia
// visit. A refusal carries the server's machine `reason`.

import { csrfToken } from "@/lib/orderActions"
import { directUpload } from "@/lib/directUpload"
import { mergeDefaults } from "@/lib/post/fieldValidation"
import { writerRateFor } from "@/lib/post/services/bookWriting"
import { money, stepIssues } from "./bookWritingSteps"
import { draftPayload, initialState } from "./draft"
import { postingCtx, serviceOf } from "./services"
import { GUEST, WORDING } from "./wording"

// The page's draft id once the guest draft exists (the cookie is the real key).
export const GUEST_DRAFT = "guest"

async function call(path, method, body) {
  const res = await fetch(path, {
    method,
    headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = new Error(data?.reason || "Request failed")
    Object.assign(err, { status: res.status, reason: data?.reason, data })
    throw err
  }
  return data
}

export const fetchQuote = (scope) => call("/post-project/quote", "POST", { scope })
export const resumeGuestDraft = () => call("/post-project/draft", "GET")
export const acceptGuestPrice = (revision, token) => call("/post-project/draft/accept-price", "POST", { revision, token })

// Bookmark: email a save link (one requestKey sends at most one email), and
// redeem one from the link's fragment, which resumes the draft as GET does.
export const emailSaveLink = ({ email, reminderConsent, requestKey }) =>
  call("/post-project/draft/save-link", "POST", {
    open_house_save_email: email, reminder_consent: reminderConsent, request_key: requestKey,
  })
export const redeemSaveLink = (token) => call("/post-project/return", "POST", { token })

// Reservation: the guest page's account screen, served at its own address
// (a full page load), where the guest creates their account to post.
export const ACCOUNT_PATH = "/post-project/account"
export const continueToAccount = () => window.location.assign(ACCOUNT_PATH)

// Where a draft already posted goes: the same address, which shows a reserved
// draft's waiting-for-confirmation seam (Key turn builds that screen).
export const openPostedProject = () => window.location.assign(ACCOUNT_PATH)

// The device that posted remembers it is waiting for confirmation, so once
// Sundown deletes the post (its cookie then finds no draft) that device shows
// the deleted-post screen rather than draft expiry. Storage may be unavailable.
const WAITING_KEY = "openHouse.waiting"
export const rememberWaiting = (waiting) => {
  try {
    if (waiting) window.localStorage.setItem(WAITING_KEY, "1")
    else window.localStorage.removeItem(WAITING_KEY)
  } catch {}
}
export const wasWaiting = () => {
  try {
    return window.localStorage.getItem(WAITING_KEY) === "1"
  } catch {
    return false
  }
}

// Post the draft at `revision` by creating an account that waits for email
// confirmation (OpenHouse::AccountsController). The password travels only in
// this request's body.
export const reserveAccount = (revision, { firstName, lastName, email, password, reminderConsent, recaptchaToken }) =>
  call(ACCOUNT_PATH, "POST", {
    open_house_first_name: firstName, open_house_last_name: lastName, open_house_account_email: email, password,
    agreement: true, reminder_consent: reminderConsent === true, revision, recaptcha_token: recaptchaToken,
  })

// Key turn's waiting screen: confirm with the email's code, which publishes
// the post and signs the guest in (answers { confirmed, redirect }), or send
// the email again (answers { sent, email, waiting }). The draft's cookie
// opens both (OpenHouse::ConfirmationsController).
export const confirmByCode = (code) => call("/post-project/confirmation", "POST", { confirmation_code: code })
export const resendConfirmation = () => call("/post-project/confirmation/resend", "POST", {})
// Forwarding address (P3-H8): { email, waiting }, the replacement sent to the new address.
export const correctEmail = (email) => call("/post-project/confirmation/email", "POST", { open_house_account_email: email })

// Where an already confirmed guest's project is (it asks a signed-out visitor to sign in).
export const PROJECTS_PATH = "/projects"

// Key turn's link page: the email's token (from the URL fragment) and the
// account's password confirm and publish the post
// (OpenHouse::ConfirmationsController#link); answers { confirmed, redirect }.
export const confirmByLink = (token, password) => call("/post-project/confirmation/link", "POST", { token, password })
// The same for an account-only enrollment (OpenHouse::EnrollmentsController#link): no project.
export const confirmEnrollmentByLink = (token, password) => call("/join/confirmation/link", "POST", { token, password })

// An address that already has an account: the continuation
// (OpenHouse::ContinuationsController) sends the guest through today's sign-in
// and back, then carries this draft into their account at Review.
export const CONTINUE_PATH = "/post-project/continue"
export const signInToContinue = () => window.location.assign(CONTINUE_PATH)

// The answers a guest draft keeps (OpenHouse::GuestDraft::ANSWER_KEYS).
const ANSWER_KEYS = [
  "fiction", "use_ai", "ai_consent", "tier", "word_count", "editing_style", "images", "add_outline", "title", "genre",
  "writing_style", "niche", "nsfw", "nsfw_details", "trigger_warning", "trigger_warning_other", "trigger_warning_warnings",
  "bundle_tier", "cover_package_type", "cover_add_ons", "cover_ai", "character_voice_count",
]

export const guestAnswers = (posting) =>
  Object.fromEntries(ANSWER_KEYS.filter((key) => posting[key] !== undefined).map((key) => [key, posting[key]]))

// The posting payload every save carries (OpenHouse::DraftsController): the
// signed-in page's draft payload for this state, hidden-answer clears included.
export const postingPayload = (state, catalog) => ({
  version: 1,
  project_posting: draftPayload(state, postingCtx(catalog, state.service, state.posting.bundle_tier)).project_posting,
})

// One save: the first creates the draft (and its cookie); later ones send the
// revision they build on, and each sends the posting payload built with its
// answers. An outline File goes straight to storage first.
export async function saveGuestDraft(revision, payload) {
  const { outline_file: file, ...openHouse } = payload.openHouse
  const draft = { service: payload.service, answers: payload.answers, step_index: payload.stepIndex, ...openHouse }
  if (file) draft.outline_file = await directUpload(file)
  const data = revision == null
    ? await call("/post-project/draft", "POST", { draft, payload: payload.posting })
    : await call("/post-project/draft", "PATCH", { revision, draft, payload: payload.posting })
  return data.draft
}

// The page's state from a saved guest draft (GuestDraft#client_json).
export function guestState(draft) {
  const fresh = initialState({}, draft, draft.service)
  return {
    ...fresh,
    draftId: GUEST_DRAFT,
    posting: mergeDefaults(serviceOf(fresh.service).defaults, draft.answers || {}),
    stepIndex: draft.stepIndex ?? 0,
  }
}

// ── price ────────────────────────────────────────────────────────────────────

// The answers that must be valid before a scope can be priced; a sample's
// book type and niche only narrow its niches.
const GATES = ["tier", "word_count", "editing_style", "bundle_tier"]
const MANUSCRIPT_GATES = [...GATES, "fiction", "use_ai"]

// The quote scope for these answers (OpenHouse::QuoteScope::FIELDS), or null
// until the answers that set the price are complete. An outline not bought
// prices as supplied, so the price never includes a fee not chosen.
export function quoteScope(service, steps, values, ctx, oh) {
  const gates = service === "sample" ? GATES : MANUSCRIPT_GATES
  if (steps.some((step) => stepIssues(step, values, ctx, oh).some(([key]) => gates.includes(key)))) return null

  const { fiction, use_ai: useAi, tier, word_count: words, editing_style: editing, niche, images } = values
  return {
    service, fiction: fiction ?? null, use_ai: !!useAi, tier, word_count: words ?? null, editing_style: editing ?? null,
    niche: niche ?? null, images: !!images, outline: useAi ? null : values.add_outline ? "buy" : "own",
    bundle_tier: values.bundle_tier ?? null, cover_package_type: values.cover_package_type ?? null,
    cover_add_ons: !!values.cover_add_ons,
  }
}

export const amountText = (amount, currency) => GUEST.amount(money(Number(amount)), currency)

// One level's writing price per 100 words, from its quoted writing line.
function per100Words(side, scope) {
  const line = side?.lines?.find((l) => l.key === "writing")
  const words = Number(scope?.word_count)
  return line && words > 0 ? (Number(line.amount) * 100) / words : null
}

// Today's rail labels for the quote's line keys, from the quoted scope.
const LINE_LABELS = {
  writing: (s) => `Book writing (${Number(s.word_count).toLocaleString()} words, ${s.tier}${s.use_ai ? ", AI" : ""})`,
  sample: (s) => `Sample (1,000 words, ${s.tier})`,
  editing: (s) => s.editing_style,
  outline: (s) => (s.fiction ? "Plot Outline" : "Professional Outline"),
  bundle: (s, ctx) => (ctx.bundles || []).find((b) => b.bundleTier === s.bundle_tier)?.name,
  cover: () => "Cover package",
  narration: () => "Audiobook Narration",
  formatting: () => "Paperback formatting",
}

// What the rail shows for a quote: the exact total and its lines, or, open to
// every writer level, the range (a sample's per-sample range; otherwise from
// the lowest level's total, as today's rail shows a range). `range` is the
// open-levels card's line (new-flow-wording §24) for book writing: per 100
// words, from the writing line of the lowest and highest levels.
export function quoteDisplay(quote, ctx) {
  const currency = quote.currency
  if (quote.openLevels) {
    const [low, high] = [quote.minimum.total, quote.maximum.total]
    const sample = quote.service === "sample"
    // Only book writing's writing line is the writer's alone; a bundle's carries its editing too.
    const writing = quote.service === "book_writing"
    const [lowRate, highRate] = writing ? [quote.minimum, quote.maximum].map((side) => per100Words(side, quote.scope)) : []
    return {
      open: true, from: !sample, lines: [],
      value: sample ? WORDING.openLevelsSamplePrice(money(Number(low)), money(Number(high))) : amountText(low, currency),
      range: lowRate != null && highRate != null ? WORDING.openLevelsPrice(money(lowRate), money(highRate)) : undefined,
    }
  }
  const lines = quote.lines.map((line) => ({
    label: LINE_LABELS[line.key]?.(quote.scope, ctx) || `[wording pending: price.line.${line.key}]`,
    amount: amountText(line.amount, currency),
  }))
  return { open: false, from: false, lines, value: amountText(quote.total, currency) }
}

// The open-levels card's range for book writing until a quote prices that
// choice: the catalog's per-100-words rates the level cards show, from Rising to
// Premium (OpenHouse::Quote::OPEN_LEVEL_BOUNDS).
export function catalogLevelsRange(service, values, ctx) {
  if (service !== "book_writing") return undefined
  const [low, high] = ["Rising", "Premium"].map((tier) => writerRateFor(tier, !!values.use_ai, ctx))
  return low != null && high != null ? WORDING.openLevelsPrice(money(low), money(high)) : undefined
}

// A quote's amounts in DraftPrice's change shape.
export const quoteAmounts = (quote) =>
  quote.openLevels ? { minimum: quote.minimum.total, maximum: quote.maximum.total } : { total: quote.total }

// The changed-price block's lines for a { previous, current, currency } change.
export function changeLines({ previous, current, currency }) {
  const m = (n) => money(Number(n))
  if (previous.minimum == null) {
    return {
      heading: GUEST.changedHeading, accept: GUEST.changedAccept,
      previous: GUEST.changedPrevious(m(previous.total), currency), current: GUEST.changedCurrent(m(current.total), currency),
    }
  }
  return {
    heading: GUEST.openChangedHeading, accept: GUEST.openChangedAccept,
    previous: GUEST.openChangedPrevious(m(previous.minimum), m(previous.maximum), currency),
    current: GUEST.openChangedCurrent(m(current.minimum), m(current.maximum), currency),
  }
}

// The outline fee the outline choice shows once bought: the quote's outline line.
export function quotedOutline(quote) {
  const line = quote?.lines?.find((l) => l.key === "outline")
  return line ? money(Number(line.amount)) : undefined
}
